import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getDashboardChartData } from '../api';

// Color palette for charts
const COLORS = ['#667eea', '#764ba2', '#11998e', '#38ef7d', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#fa709a', '#fee140'];

// Format date for display (e.g., "Jan 15")
const formatDateShort = (dateStr) => {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
};

// Format week for display (e.g., "Jan 15-21")
const formatWeekShort = (dateStr) => {
  const startDate = new Date(dateStr);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  if (startDate.getMonth() === endDate.getMonth()) {
    return `${months[startDate.getMonth()]} ${startDate.getDate()}-${endDate.getDate()}`;
  }
  return `${months[startDate.getMonth()]} ${startDate.getDate()} - ${months[endDate.getMonth()]} ${endDate.getDate()}`;
};

// Custom tooltip for line/area charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1.5 }} elevation={3}>
        <Typography variant="body2" fontWeight="bold">{formatDateShort(label)}</Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color }}>
            {entry.name}: {entry.name === 'Earnings' ? `$${entry.value.toFixed(2)}` : `${entry.value.toFixed(1)} hrs`}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

// Custom tooltip for pie chart
const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5 }} elevation={3}>
        <Typography variant="body2" fontWeight="bold">{data.company}</Typography>
        <Typography variant="body2">{data.hours.toFixed(1)} hours</Typography>
        <Typography variant="body2">${data.earnings.toFixed(2)}</Typography>
      </Paper>
    );
  }
  return null;
};

// Custom tooltip for bar chart
const BarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1.5 }} elevation={3}>
        <Typography variant="body2" fontWeight="bold">{formatWeekShort(label)}</Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color }}>
            {entry.name}: {entry.name === 'Earnings' ? `$${entry.value.toFixed(2)}` : `${entry.value.toFixed(1)} hrs`}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

export default function DashboardCharts() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState({
    dailyHours: [],
    hoursByCompany: [],
    earningsTrend: []
  });
  const [dateRange, setDateRange] = useState(14);

  const loadChartData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getDashboardChartData(dateRange);
      setChartData(res.data || { dailyHours: [], hoursByCompany: [], earningsTrend: [] });
    } catch (err) {
      setError('Failed to load chart data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadChartData();
  }, [loadChartData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasData = chartData.dailyHours.some(d => d.hours > 0) || 
                  chartData.hoursByCompany.length > 0 || 
                  chartData.earningsTrend.some(d => d.hours > 0);

  if (!hasData) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for charts. Start logging time entries to see analytics.
        </Typography>
      </Paper>
    );
  }

  return (
    <Grid container spacing={3}>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Grid>
      )}
      {/* Hours Over Time - Area Chart */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Hours Over Time</Typography>
            <ToggleButtonGroup
              value={dateRange}
              exclusive
              onChange={(e, value) => value && setDateRange(value)}
              size="small"
            >
              <ToggleButton value={7}>7 days</ToggleButton>
              <ToggleButton value={14}>14 days</ToggleButton>
              <ToggleButton value={30}>30 days</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
            <AreaChart data={chartData.dailyHours} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDateShort}
                tick={{ fontSize: 12 }}
                interval={isMobile ? 2 : 1}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}h`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="hours" 
                name="Hours"
                stroke="#667eea" 
                fillOpacity={1} 
                fill="url(#colorHours)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>

      {/* Hours by Company - Pie Chart */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Hours by Company</Typography>
          {chartData.hoursByCompany.length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
              <PieChart>
                <Pie
                  data={chartData.hoursByCompany}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 40 : 60}
                  outerRadius={isMobile ? 70 : 100}
                  fill="#8884d8"
                  paddingAngle={2}
                  dataKey="hours"
                  nameKey="company"
                  label={({ company, percent }) => 
                    isMobile ? `${(percent * 100).toFixed(0)}%` : `${company} (${(percent * 100).toFixed(0)}%)`
                  }
                  labelLine={!isMobile}
                >
                  {chartData.hoursByCompany.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <Typography color="text.secondary">No company data available</Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Weekly Earnings Trend - Bar Chart */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>Weekly Earnings Trend</Typography>
          {chartData.earningsTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
              <BarChart data={chartData.earningsTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis 
                  dataKey="week" 
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    return `${months[date.getMonth()]} ${date.getDate()}`;
                  }}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${value}h`}
                />
                <Tooltip content={<BarTooltip />} />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="earnings" 
                  name="Earnings"
                  fill="#11998e" 
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  yAxisId="right"
                  dataKey="hours" 
                  name="Hours"
                  fill="#764ba2" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <Typography color="text.secondary">No weekly data available</Typography>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
